import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import Button from '../components/Button';
import Icon from '../components/Icon';

interface Game { id: string; name: string; imageUrl: string | null; isHot?: boolean; }

const HOW_IT_WORKS: { icon: string; key: string }[] = [
  { icon: 'bolt', key: 'agentFree' },
  { icon: 'wallet', key: 'oneWallet' },
  { icon: 'bolt', key: 'fastPayout' },
];

export default function GuestLanding() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [games, setGames] = useState<Game[]>([]);

  useEffect(() => {
    axios.get<Game[]>(`${API_BASE}/games`).then(res => setGames(res.data)).catch(() => {});
  }, []);

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <div style={{ padding: '24px 20px 32px', paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))' }}>
        {/* Hero */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="/icons/icon-512.png" alt="" style={{ width: 72, height: 72, marginBottom: 12, objectFit: 'contain' }} />
          <div style={{ fontSize: 26, fontWeight: 900, color: colors.text }}>{t('auth.preview.title')}</div>
          <div style={{ fontSize: 15, color: colors.textSub, marginTop: 6, lineHeight: 1.5, maxWidth: 360, margin: '6px auto 0' }}>
            {t('auth.preview.subtitle')}
          </div>
        </div>

        {/* Games we offer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: colors.text }}>{t('auth.preview.gamesHeader')}</div>
          <button
            onClick={() => navigate('/games')}
            style={{ background: 'transparent', border: 'none', color: colors.accent, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            {t('auth.preview.seeAllGames')}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 32 }}>
          {games.slice(0, 9).map(g => (
            <div key={g.id} onClick={() => navigate('/games')} style={{
              backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
            }}>
              <div style={{ aspectRatio: '1 / 1', backgroundColor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {g.imageUrl
                  ? <img src={g.imageUrl} alt={g.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <Icon name="gamepad" size={22} color={colors.textSub} />}
              </div>
              <div style={{ padding: '7px 8px', fontSize: 12, fontWeight: 700, color: colors.text, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {g.name}
              </div>
            </div>
          ))}
        </div>

        {/* How it works / why AGA */}
        <div style={{ fontSize: 16, fontWeight: 800, color: colors.text, marginBottom: 12 }}>{t('auth.preview.howTitle')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
          {HOW_IT_WORKS.map(s => (
            <div key={s.key} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`, borderRadius: 12, padding: 14,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                backgroundColor: colors.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={s.icon} size={17} color={colors.accentText} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: colors.text }}>{t(`auth.preview.steps.${s.key}.title`)}</div>
                <div style={{ fontSize: 13, color: colors.textSub, marginTop: 3, lineHeight: 1.45 }}>{t(`auth.preview.steps.${s.key}.body`)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: colors.text }}>{t('auth.preview.ctaTitle')}</div>
          <div style={{ fontSize: 13, color: colors.textSub, marginTop: 4 }}>{t('auth.preview.ctaSubtitle')}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
          <Button label={t('auth.guest.register')} icon="user-plus" onClick={() => navigate('/register')} />
          <Button label={t('auth.guest.login')} icon="sign-in-alt" variant="outline" onClick={() => navigate('/login')} />
        </div>
      </div>
    </div>
  );
}
